// Hands-free on screen (Features 4, David, 26 Oct): one mark for it wherever it is on (under the chat box beside the
// model, and on a Button after its words), the chat's own switch in its menu (the line under the box), the switch on a Button or a job,
// and the default for new ones in Settings, Agents. The mark is small and quiet, in the board's own icons, so it is
// never on by surprise and never alarming. When the linked computer's runner is too old to know it, each place says
// to update the runner in place of a switch that would do nothing.

import { useId, useState } from "react";
import { FastForward } from "lucide-react";
import { HANDS_FREE } from "../../shared/withoutAsking";
import { Switch } from "../chrome/ui";
import { cx } from "../lib/util";
import { toast, useStore } from "../state/store";
import { chatHandsFree, handsFreeDefault, runnerHandsFree, runnerHandsFreeJobs, setChatHandsFree, setHandsFreeDefault } from "./handsFree";
import { OwnMcpsSetting } from "./OwnMcps";
import { useChat } from "./store";
import { Why } from "./Why";

/** The one mark for Hands-free: the board's icon set, drawn bold enough to read at 1x. */
export function HandsFreeIcon({ className, size = 12 }: { className?: string; size?: number }) {
  return <FastForward className={cx("hf-icon", className)} size={size} strokeWidth={2.4} aria-hidden />;
}

/** Whether the computer the chat runs on is too old for Hands-free (for a job, too old to hold it to its rule): the place says to update in place of a switch. */
function useOldRunner(onRunner: boolean, job = false): string | null {
  const runner = useStore((s) => s.runner);
  if (!onRunner || !runner?.online || (job ? runnerHandsFreeJobs(runner) : runnerHandsFree(runner))) return null;
  return runner.host ?? "your computer";
}

/** The chat's own switch, in its menu (the line under the box): for this chat, kept with it. */
export function HandsFreeMenu({ onRunner }: { onRunner: boolean }) {
  const runner = useStore((s) => s.runner);
  const saved = useChat((s) => s.handsFree);
  const on = chatHandsFree({ handsFree: saved }, runner);
  const old = useOldRunner(onRunner);
  const switchId = useId();
  // what it does, and where new chats' default lives, behind the ? (Fixes 8, David: "far fewer words")
  const why = `Uses your tools without stopping to ask you. ${handsFreeDefault(runner) ? "New chats start hands-free" : "New chats start asking"}: change that in Settings, Agents.`;
  return (
    <div className="hf-menu">
      <div className="chat-set">
        <label className="chat-set-name" htmlFor={old ? undefined : switchId}>
          <HandsFreeIcon size={12} />
          Hands-free
          <Why text={old ? HANDS_FREE.updateHow(old) : why} />
        </label>
        {old ? null : (
          <button type="button" id={switchId} role="switch" aria-checked={on} aria-label="Run this chat hands-free" className={cx("switch", on && "is-on")} onClick={() => setChatHandsFree(!on)}>
            <i />
          </button>
        )}
      </div>
      {old ? <p className="chat-set-sub is-warn">{HANDS_FREE.update}</p> : on && <p className="chat-set-sub">Still asks before sending, deleting or buying</p>}
    </div>
  );
}

/**
 * The switch on a Button or a job, with what it does in a line; a Button's second option under it while it is on.
 * sends is null where there is no second option (a job). A job's own tools are set right under it (agent): Use my
 * existing MCPs, so a hands-free job is set up in one place (Fixes 8, David: "it should be easy to set up").
 */
export function HandsFreeSwitch({ on, onChange, sends, onSends, job = false, agent }: { on: boolean; onChange: (v: boolean) => void; sends: boolean | null; onSends?: (v: boolean) => void; job?: boolean; agent?: "claude" | "codex" }) {
  const old = useOldRunner(true, job);
  return (
    <div className={cx("own-mcps hf-setting", on && "is-on")}>
      <Switch label={<span className="hf-switch-label"><HandsFreeIcon />Hands-free</span>} checked={on} onChange={onChange} />
      <p className="own-mcps-sub">{!on ? (job ? HANDS_FREE.offJob : HANDS_FREE.off) : job ? HANDS_FREE.onJob : sends ? HANDS_FREE.onSends : HANDS_FREE.on}</p>
      {on && sends !== null && onSends && (
        <div className="hf-sends">
          <Switch label={HANDS_FREE.sendsLabel} checked={sends} onChange={onSends} />
          <p className="own-mcps-sub">{sends ? HANDS_FREE.sendsOn : HANDS_FREE.sendsOff}</p>
        </div>
      )}
      {on && job && agent && (
        <div className="hf-sends">
          <OwnMcpsSetting agent={agent} compact job />
        </div>
      )}
      {on && old && <p className="own-mcps-sub is-warn">{`${HANDS_FREE.update}. ${HANDS_FREE.updateHow(old)}`}</p>}
    </div>
  );
}

/** Settings, Agents: the default for new chats, Buttons and jobs, off to begin with, on every device. */
export function HandsFreeSettings() {
  const on = useStore((s) => handsFreeDefault(s.runner));
  const [busy, setBusy] = useState(false);
  const flip = async (v: boolean) => {
    setBusy(true);
    try {
      await setHandsFreeDefault(v);
    } catch (e) {
      toast({ level: "warn", title: "Hands-free's default did not change", body: (e as Error)?.message ?? "Check your connection and try again." });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="agent-models hf-settings" aria-busy={busy}>
      <h4 className="notify-h">Hands-free</h4>
      <Switch label="Start new chats, Buttons and jobs hands-free" checked={on} onChange={(v) => void flip(v)} />
      <p className="note">
        Agents use your tools without asking, except to send, delete or buy.
      </p>
    </div>
  );
}
